import { useState } from 'react';
import {
  AlertDialog,
  Body,
  Button,
  Dialog,
  DialogFooter,
  DialogSection,
  DropdownMenu,
  DropdownMenuItem,
  Field,
  I,
  Input,
} from '@leanin/ui';

export interface ScheduleSendButtonProps {
  readonly disabled?: boolean;
  readonly pending?: boolean;
  readonly onSendNow: () => Promise<void> | void;
  readonly onSchedule: (scheduledIsoUtc: string) => Promise<void> | void;
}

export function ScheduleSendButton({
  disabled,
  pending,
  onSendNow,
  onSchedule,
}: ScheduleSendButtonProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const openSchedule = () => {
    setScheduleError(null);
    setScheduledFor('');
    setScheduleOpen(true);
  };

  const confirmSchedule = async () => {
    if (scheduledFor.length === 0) {
      setScheduleError('Pick a date and time.');
      return;
    }
    const target = new Date(scheduledFor);
    if (Number.isNaN(target.getTime())) {
      setScheduleError('Invalid date.');
      return;
    }
    if (target.getTime() <= Date.now()) {
      setScheduleError('Scheduled time must be in the future.');
      return;
    }
    await onSchedule(target.toISOString());
    setScheduleOpen(false);
  };

  return (
    <>
      <DropdownMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        align="end"
        trigger={
          <Button
            variant="primary"
            size="md"
            iconRight={I.arrowDown}
            disabled={disabled || pending}
            loading={pending}
          >
            Send
          </Button>
        }
      >
        <DropdownMenuItem onSelect={() => setConfirmSendOpen(true)}>
          Send now
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={openSchedule}>
          Schedule for…
        </DropdownMenuItem>
      </DropdownMenu>

      <AlertDialog
        open={confirmSendOpen}
        onOpenChange={setConfirmSendOpen}
        title="Send this broadcast now?"
        description="Members will receive this broadcast immediately. This can't be undone."
        actionLabel="Send now"
        cancelLabel="Cancel"
        onAction={() => {
          setConfirmSendOpen(false);
          void onSendNow();
        }}
      />

      <Dialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        title="Schedule for…"
        description="Pick when to send this broadcast. Members will receive it at the chosen time."
        size="sm"
      >
        <DialogSection gap="md">
          <Field label="Date and time" required>
            <Input
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => {
                setScheduledFor(e.target.value);
                setScheduleError(null);
              }}
              autoFocus
            />
          </Field>
          {scheduleError !== null ? (
            <Body size="sm" color="destructive">
              {scheduleError}
            </Body>
          ) : null}
        </DialogSection>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setScheduleOpen(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            iconRight={I.cal}
            onClick={() => void confirmSchedule()}
            loading={pending}
            disabled={pending}
          >
            Schedule
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
}
